import { useMemo } from 'react';
import { useField } from 'formik';
import { useTranslation } from 'react-i18next';

import { defaultLanguage, useCourseContext } from 'context';
import {
  CheckboxFormField,
  FormChoiceResponse,
  FormChoiceResponseType,
  FORM_OTHER_OPTION_VALUE,
} from 'types';

import { Input } from '../input';
import { CheckboxField } from '../fields';

export const FormCheckboxField = ({
  id,
  label,
  options,
  required,
  translations,
}: CheckboxFormField) => {
  const { language } = useCourseContext();
  const { t } = useTranslation('common');
  const otherLabel = t('choiceOther');
  const [field, , helpers] = useField<FormChoiceResponse[]>(id);
  const responses = Array.isArray(field.value) ? field.value : [];
  const otherResponse = responses.find(
    (response) => response.type === FormChoiceResponseType.OTHER
  );
  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        label,
        options:
          options?.options.map((value) => ({ value, label: value })) ?? [],
      };
    }

    const translation = translations[language];
    const langOptions = translation?.options || options?.options;
    return {
      label: label ? translation?.label || label : label,
      options:
        langOptions?.map((value, index) => ({
          label: value,
          value: options?.options?.[index] || value,
        })) ?? [],
    };
  }, [label, options?.options, language, translations]);

  const displayValue = responses.map((response) =>
    response.type === FormChoiceResponseType.OTHER
      ? FORM_OTHER_OPTION_VALUE
      : response.value
  );
  const checkboxOptions = [
    ...translation.options,
    ...(options?.allowOther
      ? [{ label: otherLabel, value: FORM_OTHER_OPTION_VALUE }]
      : []),
  ];

  return (
    <CheckboxField
      name={id}
      required={required}
      label={translation.label}
      options={checkboxOptions}
      displayValue={displayValue}
      mapValue={(values) =>
        values.map((value) =>
          value === FORM_OTHER_OPTION_VALUE
            ? {
                // Preserve typed Other text when a different option changes.
                value: otherResponse?.value ?? '',
                type: FormChoiceResponseType.OTHER,
              }
            : { value, type: FormChoiceResponseType.OPTION }
        )
      }
    >
      {options?.allowOther && otherResponse && (
        <div className="mt-2">
          <Input
            fullWidth
            label={otherLabel}
            value={otherResponse.value}
            placeholder={t('choiceOtherPlaceholder')}
            onBlur={() => helpers.setTouched(true, true)}
            onChange={(event) =>
              helpers.setValue(
                responses.map((response) =>
                  response.type === FormChoiceResponseType.OTHER
                    ? { ...response, value: event.target.value }
                    : response
                ),
                true
              )
            }
          />
        </div>
      )}
    </CheckboxField>
  );
};
